import React, { useEffect, useMemo, useRef, useState } from "react";
import { ALL_CURRENCIES, loadFavoriteCurrencies, saveFavoriteCurrencies, getCurrencyMeta } from "@/lib/currencies";
import { api } from "@/lib/apiClient";
import { Star, MagnifyingGlass, CaretDown } from "@phosphor-icons/react";

// Cache the backend favorites at module level so multiple pickers stay in sync
let _serverFavorites = null;
const _listeners = new Set();
function _notify() { _listeners.forEach((fn) => fn(_serverFavorites)); }
async function _loadServerFavoritesOnce() {
    if (_serverFavorites) return _serverFavorites;
    try {
        const { data } = await api.get("/users/me/preferences");
        if (Array.isArray(data?.favorite_currencies) && data.favorite_currencies.length > 0) {
            _serverFavorites = data.favorite_currencies;
            saveFavoriteCurrencies(_serverFavorites);
            _notify();
        }
    } catch (e) {
        // fall back to localStorage
    }
    return _serverFavorites;
}
function _saveServerFavorites(list) {
    _serverFavorites = list;
    saveFavoriteCurrencies(list);
    _notify();
    // fire-and-forget — UI already updated
    api.put("/users/me/preferences", { favorite_currencies: list }).catch(() => {});
}

/**
 * Searchable currency picker with favorites group at top.
 * Props:
 *  - value: ISO code, e.g. "USD"
 *  - onChange: (code) => void
 *  - testid?: string (default 'currency-picker')
 *  - className?: trigger button extra classes
 */
export default function CurrencyPicker({ value, onChange, testid = "currency-picker", className = "" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [favorites, setFavorites] = useState(() => loadFavoriteCurrencies());
    const rootRef = useRef(null);

    // Initial sync from backend
    useEffect(() => {
        let active = true;
        _loadServerFavoritesOnce().then((list) => {
            if (active && list && list.length > 0) setFavorites(list);
        });
        const fn = (list) => { if (list) setFavorites(list); };
        _listeners.add(fn);
        return () => { active = false; _listeners.delete(fn); };
    }, []);

    useEffect(() => {
        function onDoc(e) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const toggleFav = (code) => {
        const next = favorites.includes(code) ? favorites.filter((c) => c !== code) : [...favorites, code];
        setFavorites(next);
        _saveServerFavorites(next);
    };

    const { favList, otherList } = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matches = (c) => !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
        const favSet = new Set(favorites);
        const fav = ALL_CURRENCIES.filter((c) => favSet.has(c.code) && matches(c));
        // Order favorites in the same order the user added them
        fav.sort((a, b) => favorites.indexOf(a.code) - favorites.indexOf(b.code));
        const others = ALL_CURRENCIES.filter((c) => !favSet.has(c.code) && matches(c));
        return { favList: fav, otherList: others };
    }, [favorites, query]);

    const current = getCurrencyMeta(value);

    return (
        <div className="relative" ref={rootRef} data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen((s) => !s)}
                data-testid={`${testid}-trigger`}
                className={`w-full h-10 border border-slate-300 px-3 inline-flex items-center justify-between bg-white text-left text-sm hover:border-slate-900 ${className}`}
            >
                <span className="font-mono">
                    <span className="text-slate-900">{current.code}</span>
                    <span className="text-slate-400 ml-2">{current.symbol}</span>
                    <span className="text-slate-500 ml-2 text-xs hidden md:inline">{current.name}</span>
                </span>
                <CaretDown size={12} className="text-slate-400" />
            </button>

            {open && (
                <div
                    className="absolute z-50 mt-1 w-[360px] max-w-[90vw] bg-white border border-slate-200 shadow-xl"
                    data-testid={`${testid}-panel`}
                >
                    <div className="border-b border-slate-200 px-3 py-2 flex items-center gap-2">
                        <MagnifyingGlass size={14} className="text-slate-400" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search code or name…"
                            className="flex-1 text-sm outline-none bg-transparent font-mono placeholder:text-slate-400"
                            data-testid={`${testid}-search`}
                        />
                    </div>
                    <div className="max-h-[360px] overflow-y-auto scrollbar-thin">
                        {favList.length > 0 && (
                            <>
                                <SectionLabel label={`Favorites · ${favList.length}`} />
                                {favList.map((c) => (
                                    <Row
                                        key={c.code}
                                        c={c}
                                        active={c.code === value}
                                        favorite
                                        onPick={() => { onChange(c.code); setOpen(false); setQuery(""); }}
                                        onToggleFav={() => toggleFav(c.code)}
                                    />
                                ))}
                            </>
                        )}
                        <SectionLabel label={`All currencies · ${otherList.length}`} />
                        {otherList.length === 0 && (
                            <div className="px-4 py-3 text-xs text-slate-500">No matches.</div>
                        )}
                        {otherList.map((c) => (
                            <Row
                                key={c.code}
                                c={c}
                                active={c.code === value}
                                onPick={() => { onChange(c.code); setOpen(false); setQuery(""); }}
                                onToggleFav={() => toggleFav(c.code)}
                            />
                        ))}
                    </div>
                    <div className="border-t border-slate-200 px-3 py-2 flex items-center justify-between bg-slate-50">
                        <span className="overline text-slate-500" style={{ fontSize: 9 }}>
                            ★ click star to favorite · saved locally
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionLabel({ label }) {
    return (
        <div className="px-4 pt-3 pb-1 overline text-slate-500" style={{ fontSize: 9 }}>
            {label}
        </div>
    );
}

function Row({ c, active, favorite, onPick, onToggleFav }) {
    return (
        <div
            className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-slate-50 ${active ? "bg-slate-100" : ""}`}
            data-testid={`currency-row-${c.code}`}
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
                className="p-1 -ml-1 text-slate-400 hover:text-amber-500"
                title={favorite ? "Remove from favorites" : "Add to favorites"}
                data-testid={`currency-fav-${c.code}`}
            >
                <Star size={14} weight={favorite ? "fill" : "regular"} className={favorite ? "text-amber-500" : ""} />
            </button>
            <button
                type="button"
                onClick={onPick}
                className="flex-1 flex items-center justify-between text-left"
                data-testid={`currency-pick-${c.code}`}
            >
                <span>
                    <span className="font-mono text-sm text-slate-900">{c.code}</span>
                    <span className="font-mono text-xs text-slate-400 ml-2">{c.symbol}</span>
                </span>
                <span className="text-xs text-slate-500 truncate ml-3">{c.name}</span>
            </button>
        </div>
    );
}
